import { useCallback, useEffect, useRef, useState } from "react";
import { Check, FileText, Loader2, Pencil, Plus, Share2, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { TEAL } from "@/lib/copilot";
import { useHub } from "@/components/AppShell";
import { Loader, PageHeader } from "@/components/common";
import { CopilotTabs } from "@/pages/Copilot";

const AUD = { everyone: "Everyone", adults: "Adults only", parents: "Parents only" };

function AudiencePick({ value, onChange, testid }) {
  return (
    <select className="q-input w-auto min-h-[44px]" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
      {Object.entries(AUD).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}

function FactRow({ f, base, reload }) {
  const act = (fn, ok) => fn().then(() => { if (ok) toast.success(ok); reload(); }).catch((e) => toast.error(errMsg(e)));
  const edit = () => { const t = window.prompt("Edit fact", f.text); if (t !== null) act(() => api.patch(`${base}/${f.id}`, { text: t }), "Saved"); };
  const share = () => { const a = window.prompt("Share with: everyone, adults or parents", "everyone"); if (a) act(() => api.post(`${base}/${f.id}/share`, { audience: a.trim().toLowerCase() }), "Shared to Family memory"); };
  return (
    <li className="flex flex-wrap items-center gap-2 rounded-xl border border-q-border bg-q-surface px-3 py-2" data-testid={`fact-${f.id}`}>
      <span className="min-w-0 flex-1 break-words text-sm text-q-text" data-testid="fact-text">{f.text}</span>
      {f.audience && <span className="rounded-full px-2 py-0.5 text-[11px] font-bold" style={{ background: `rgba(${TEAL},0.14)`, color: `rgb(${TEAL})` }} data-testid="fact-audience">{AUD[f.audience]}</span>}
      {f.status === "pending" && <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-bold text-amber-300" data-testid="fact-pending">Waiting for a grown-up{f.owner_kind === "kid" ? ` · ${f.owner_name}` : ""}</span>}
      <span className="text-[11px] text-q-faint">{f.owner_name}</span>
      {f.can_approve && <button className="q-btn q-btn-secondary min-h-[44px] px-2 text-xs" onClick={() => act(() => api.post(`${base}/${f.id}/approve`), "Approved")} data-testid={`fact-approve-${f.id}`}><Check className="h-3.5 w-3.5" /> Approve</button>}
      {f.can_share && <button className="q-btn q-btn-ghost min-h-[44px] px-2 text-xs" onClick={share} data-testid={`fact-share-${f.id}`}><Share2 className="h-3.5 w-3.5" /> Share to family</button>}
      {f.can_edit && <button className="inline-flex h-11 w-11 items-center justify-center rounded-full text-q-faint hover:text-q-text" aria-label="Edit" onClick={edit} data-testid={`fact-edit-${f.id}`}><Pencil className="h-3.5 w-3.5" /></button>}
      {f.can_edit && <button className="inline-flex h-11 w-11 items-center justify-center rounded-full text-q-faint hover:text-red-300" aria-label="Delete" onClick={() => act(() => api.delete(`${base}/${f.id}`), "Deleted")} data-testid={`fact-delete-${f.id}`}><Trash2 className="h-3.5 w-3.5" /></button>}
    </li>
  );
}

function AddFact({ onAdd, withAudience, testid }) {
  const [t, setT] = useState("");
  const [a, setA] = useState("everyone");
  return (
    <form className="mb-3 flex flex-wrap gap-2" onSubmit={(e) => { e.preventDefault(); if (t.trim()) onAdd(t, a).then(() => setT("")); }}>
      <input className="q-input min-h-[44px] min-w-[200px] flex-1" maxLength={300} placeholder="e.g. Pizza night is every Friday" value={t} onChange={(e) => setT(e.target.value)} data-testid={`${testid}-input`} />
      {withAudience && <AudiencePick value={a} onChange={setA} testid={`${testid}-audience`} />}
      <button className="q-btn q-btn-primary min-h-[44px]" data-testid={`${testid}-btn`}><Plus className="h-4 w-4" /> Add</button>
    </form>
  );
}

function Docs({ hubId, canUpload }) {
  const [docs, setDocs] = useState(null);
  const [aud, setAud] = useState("everyone");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const input = useRef(null);
  const base = `/organizations/${hubId}/copilot/documents`;
  const load = useCallback(() => api.get(base).then(({ data }) => setDocs(data.documents)), [base]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!docs?.some((d) => d.status === "processing")) return undefined;
    const t = setInterval(load, 2000);
    return () => clearInterval(t);
  }, [docs, load]);
  const upload = async (file) => {
    setError("");
    if (!/\.(pdf|docx|txt|md)$/i.test(file.name) || file.size > 20 * 1024 * 1024) { setError("Upload a PDF, DOCX, TXT or MD under 20 MB"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("audience", aud);
    try { await api.post(base, fd); load(); } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };
  if (!docs) return <Loader />;
  return (
    <section className="q-card" data-testid="documents-section">
      <h2 className="mb-3 font-display text-lg font-extrabold text-q-text">Documents</h2>
      {canUpload && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <AudiencePick value={aud} onChange={setAud} testid="doc-audience" />
          <button className="q-btn q-btn-secondary min-h-[44px]" disabled={busy} onClick={() => input.current?.click()} data-testid="doc-upload-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload document</button>
          <input ref={input} type="file" hidden accept=".pdf,.docx,.txt,.md" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) upload(f); }} data-testid="doc-file-input" />
          <span className="text-xs text-q-faint">PDF, DOCX, TXT or MD · up to 20 MB · {docs.length}/50</span>
        </div>
      )}
      {error && <p className="q-error mb-3" data-testid="doc-error">{error}</p>}
      <ul className="space-y-1.5" data-testid="documents-list">
        {docs.length === 0 && <li className="text-sm text-q-faint">No documents yet.</li>}
        {docs.map((d) => (
          <li key={d.id} className="flex flex-wrap items-center gap-2 rounded-xl border border-q-border bg-q-surface px-3 py-2" data-testid={`doc-${d.id}`} data-status={d.status}>
            <FileText className="h-4 w-4 flex-shrink-0" style={{ color: `rgb(${TEAL})` }} />
            <span className="min-w-0 flex-1 truncate text-sm font-semibold text-q-text" data-testid="doc-name">{d.name}</span>
            <span className="rounded-full px-2 py-0.5 text-[11px] font-bold" style={{ background: `rgba(${TEAL},0.14)`, color: `rgb(${TEAL})` }}>{AUD[d.audience]}</span>
            <span className={`text-[11px] font-bold ${d.status === "failed" ? "text-red-300" : "text-q-faint"}`} data-testid="doc-status">{d.status === "processing" ? "Processing…" : d.status === "failed" ? d.error : "Ready"}</span>
            {d.can_delete && <button className="inline-flex h-11 w-11 items-center justify-center rounded-full text-q-faint hover:text-red-300" aria-label="Delete" onClick={() => api.delete(`${base}/${d.id}`).then(load).catch((e) => toast.error(errMsg(e)))} data-testid={`doc-delete-${d.id}`}><Trash2 className="h-3.5 w-3.5" /></button>}
          </li>
        ))}
      </ul>
    </section>
  );
}

export default function CopilotMemory() {
  const { hub } = useHub();
  const [m, setM] = useState(null);
  const base = `/organizations/${hub.id}/copilot/memory`;
  const load = useCallback(() => api.get(base).then(({ data }) => setM(data)), [base]);
  useEffect(() => { load(); }, [load]);
  if (!m) return <Loader />;
  const add = (path, body) => api.post(`${base}/${path}`, body).then(load).catch((e) => { toast.error(errMsg(e)); throw e; });
  const Section = ({ title, hint, rows, children, testid }) => (
    <section className="q-card" data-testid={testid}>
      <h2 className="font-display text-lg font-extrabold text-q-text">{title}</h2>
      <p className="mb-3 text-xs text-q-muted">{hint}</p>
      {children}
      <ul className="space-y-1.5">{rows.length === 0 ? <li className="text-sm text-q-faint">Nothing yet.</li> : rows.map((f) => <FactRow key={f.id} f={f} base={base} reload={load} />)}</ul>
    </section>
  );
  return (
    <div data-testid="copilot-memory-page">
      <PageHeader eyebrow="Copilot" title="Memory & documents" rgb={TEAL} testid="copilot-memory-title" />
      <CopilotTabs active="memory" canSeeKids={hub.my_role === "owner" || hub.my_role === "coparent"} />
      <div className="grid gap-4 xl:grid-cols-2">
        <Section title="Family memory" hint="Facts Copilot uses for the people you choose." rows={m.family} testid="family-memory">
          {m.can_add_family && <AddFact withAudience testid="family-fact-add" onAdd={(text, audience) => add("family", { text, audience })} />}
        </Section>
        <Section title="My memory" hint="Private to you. Copilot uses these only in your chats." rows={m.mine} testid="my-memory">
          <AddFact testid="my-fact-add" onAdd={(text) => add("mine", { text })} />
        </Section>
        <Section title="Learned facts" hint="Picked up from your chats. Kids' facts wait for a parent before Copilot uses them." rows={m.learned} testid="learned-facts" />
        <Docs hubId={hub.id} canUpload={m.can_upload} />
      </div>
    </div>
  );
}
